import { Card, Flex, Grid, Text } from "@radix-ui/themes";
import cx from "classnames";
import { type FC } from "react";

import { type AutomationSummary } from "../mockData";

export interface AuditSummaryStats {
  totalWrikeTasks: number;
  totalReviewCycles: number;
  totalTaskChecks: number;
  dateRangeStart: string | null;
  dateRangeEnd: string | null;
}

interface SummaryCardsProps {
  summary: AutomationSummary;
  summaryStats?: AuditSummaryStats;
}

interface BreakdownCardProps {
  label: string;
  value: number;
  variant: "green" | "gold" | "gray";
  testId?: string;
}

function BreakdownCard({ label, value, variant, testId }: BreakdownCardProps) {
  const textStyles = {
    green: "text-grass-grass10",
    gold: "text-puntt-gold-10",
    gray: "text-puntt-neutral-gray-9",
  };

  return (
    <Card className="flex-1" data-testid={testId}>
      <Flex align="center" justify="between">
        <Text size="2" weight="medium">
          {label}
        </Text>
      </Flex>
      <Flex direction="column" gap="2" className="p-4">
        <Flex justify="center">
          <Text
            size="9"
            weight="medium"
            className={cx("tabular-nums", textStyles[variant])}
            data-testid="roi-summary-value"
          >
            {value}
          </Text>
        </Flex>
      </Flex>
    </Card>
  );
}

function formatShortDate(dateString: string | null): string {
  if (!dateString) return "—";
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

export const SummaryCards: FC<SummaryCardsProps> = ({
  summary,
  summaryStats,
}) => {
  return (
    <Flex direction="column" gap="4" data-testid="roi-summary-cards">
      <Grid columns="2" gap="4">
        {/* Current Automation Card */}
        <Card className="flex flex-col bg-base-white" data-testid="roi-summary-card-automation">
          <Text size="3" weight="bold">
            Current Automation
          </Text>
          <Flex
            direction="column"
            align="center"
            justify="center"
            gap="2"
            className="flex-1"
          >
            <Flex align="baseline" gap="1">
              <Text
                size="9"
                weight="medium"
                className="tabular-nums text-grass-grass10"
                data-testid="roi-summary-value"
              >
                {summary.currentAutomationPercent}%
              </Text>
            </Flex>
            <Text size="5" weight="medium">
              of review tasks automated
            </Text>
          </Flex>
        </Card>

        {/* Task Breakdown Section */}
        <Card className="bg-base-white">
          <Text size="3" weight="bold" className="mb-3 block">
            Automated Review Task Breakdown
          </Text>
          <Flex gap="3" align="stretch">
            <BreakdownCard
              label="Fully Automated"
              value={summary.fullyAutomatedCount}
              variant="green"
              testId="roi-summary-card-fully-automated"
            />

            <BreakdownCard
              label="In Training"
              value={summary.inTrainingCount}
              variant="gold"
              testId="roi-summary-card-training"
            />

            <BreakdownCard
              label="Out of Scope"
              value={summary.outOfScopeCount}
              variant="gray"
              testId="roi-summary-card-out-of-scope"
            />
          </Flex>
        </Card>
      </Grid>

      {/* Summary Stats Paragraph */}
      {summaryStats && (
        <Text size="2" color="gray">
          Analyzing{" "}
          <Text weight="medium" color="gray">
            {summaryStats.totalWrikeTasks.toLocaleString()} Wrike tasks
          </Text>{" "}
          across{" "}
          <Text weight="medium" color="gray">
            {summaryStats.totalReviewCycles.toLocaleString()} review cycles
          </Text>{" "}
          with{" "}
          <Text weight="medium" color="gray">
            {summaryStats.totalTaskChecks.toLocaleString()} task checks
          </Text>
          {summaryStats.dateRangeStart && summaryStats.dateRangeEnd && (
            <>
              {" "}
              from {formatShortDate(summaryStats.dateRangeStart)} to{" "}
              {formatShortDate(summaryStats.dateRangeEnd)}
            </>
          )}
          .
        </Text>
      )}
    </Flex>
  );
};
